import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card_Widget';
import { BarChart} from '@/components/charts';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useNavigate } from 'react-router-dom';
import { mockSocialImpactStatsCharity } from '@/utils/mockData';

interface Donation {
  id: string;
  environmental_impact: number;
  social_impact: number;
  economic_impact: number;
  amount: number;
  created_at: string;
  businesses?: {
    name: string;
  };
}

interface Event {
  id: string;
  name: string;
  impact_score: number;
  date: string;
  volunteer_count: number;
}

interface SocialImpactStats {
  totalDonations: number;
  totalEvents: number;
  totalVolunteers: number;
  impactMetrics: {
    environmental: number;
    social: number;
    economic: number;
  };
  recentActivities: Array<{
    id: string;
    type: 'donation' | 'event' | 'volunteer';
    title: string;
    impact: number;
    date: string;
    business_name?: string;
  }>;
}

export default function SocialImpactDashboardCharity() {
  const { user } = useAuthV2();
  const navigate = useNavigate();
  const [stats, setStats] = useState<SocialImpactStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSocialImpactStats();
  }, [user]);

  const fetchSocialImpactStats = async () => {
    setLoading(true);
    try {
      // Get current user with normalizeUser
      const { createClient, normalizeUser } = await import('@/services/supabase');
      const supabase = createClient();
      const { data: { session } } = await (await supabase).auth.getSession();
      
      // Check if we have a valid session
      if (!session?.user) {
        console.error('No valid Supabase session found');
        setLoading(false);
        return;
      }
      
      // Use normalizeUser to get consistent user data
      const normalizedUser = normalizeUser(session.user);
      const isSuperAdmin = normalizedUser.role === 'super_admin';
      
      if (isSuperAdmin) {
        // For super admin, use mock data
        console.log('Using mock social impact data for super admin');
        
        // Short timeout to simulate API call
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Set mock stats with type casting to ensure compatibility
        const mockStats = mockSocialImpactStatsCharity as SocialImpactStats;
        
        // Count event attendees from mock data
        const totalAttendees = mockStats.recentActivities
          .filter(activity => activity.type === 'event')
          .reduce((total, activity) => total + activity.impact, 0);
          
        // Make sure volunteer count aligns with event attendees
        mockStats.totalVolunteers = totalAttendees;
        
        setStats(mockStats);
      } else {
        // For regular users, use real API
        // Supabase client is already created above
        
        // Fetch donations using normalized user business_id (for charity, business_id is used as charity_id)
        const { data: donations, error: donationsError } = await (await supabase)
          .from('donations')
          .select('*, businesses(name)')
          .eq('charity_id', normalizedUser.business_id);

        if (donationsError) throw donationsError;

        // Fetch events using the same Supabase client with normalized user business_id
        const { data: events, error: eventsError } = await (await supabase)
          .from('events')
          .select('*')
          .eq('charity_id', normalizedUser.business_id);

        if (eventsError) throw eventsError;

        // Calculate impact metrics
        const environmental = donations.reduce((acc: number, curr: Donation) => acc + (curr.environmental_impact || 0), 0);
        const social = donations.reduce((acc: number, curr: Donation) => acc + (curr.social_impact || 0), 0);
        const economic = donations.reduce((acc: number, curr: Donation) => acc + (curr.economic_impact || 0), 0);

        // Combine recent activities
        const recentActivities = [
          ...donations.map((d: Donation) => ({
            id: d.id,
            type: 'donation' as 'donation' | 'event' | 'volunteer',
            title: `Donation from ${d.businesses?.name || 'Anonymous'}`,
            impact: d.amount,
            date: d.created_at,
            business_name: d.businesses?.name,
          })),
          ...events.map((e: Event) => ({
            id: e.id,
            type: 'event' as 'donation' | 'event' | 'volunteer',
            title: e.name,
            impact: e.impact_score,
            date: e.date,
          })),
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
         .slice(0, 5);

        setStats({
          totalDonations: donations.length,
          totalEvents: events.length,
          totalVolunteers: events.reduce((acc: number, curr: Event) => acc + (curr.volunteer_count || 0), 0),
          impactMetrics: {
            environmental,
            social,
            economic,
          },
          recentActivities,
        });
      }
    } catch (error) {
      console.error('Error fetching social impact stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!stats) return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <button
            type="button"
            onClick={() => navigate('/charity/dashboard')}
            className="mr-4 text-gray-500 hover:text-navy-blue"
          >
          </button>
          <h1 className="text-xl font-medium text-navy-blue">Social Impact Dashboard</h1>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center mb-6">
          <button
            type="button"
            onClick={() => navigate('/charity/dashboard')}
            className="mr-4 text-gray-500 hover:text-navy-blue"
          >
        
          </button>
          <h1 className="text-xl font-medium text-navy-blue">Social Impact Dashboard</h1>
        </div>
      
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
          <CardHeader>
            <CardTitle>Total Donations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalDonations}</div>
            <div className="text-sm text-gray-500">donations received</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalEvents}</div>
            <div className="text-sm text-gray-500">events organized</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Volunteers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{stats.totalVolunteers}</div>
            <div className="text-sm text-gray-500">volunteers engaged</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Total Attendees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">
              {stats.totalVolunteers}
            </div>
            <div className="text-sm text-gray-500">event attendees</div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Impact Distribution by Category</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChart
              data={[
                { name: 'Environmental', value: stats.impactMetrics.environmental },
                { name: 'Social', value: stats.impactMetrics.social },
                { name: 'Economic', value: stats.impactMetrics.economic },
              ]}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.recentActivities.map((activity) => (
                <div key={activity.id} className="flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{activity.title}</div>
                    <div className="text-sm text-gray-500">
                      {new Date(activity.date).toLocaleDateString()}
                    </div>
                    {activity.business_name && (
                      <div className="text-sm text-blue-600">
                        Business: {activity.business_name}
                      </div>
                    )}
                  </div>
                  <div className="text-green-600 font-semibold">
                    {activity.type === 'event' ? `${activity.impact} attendees` : `${activity.impact} impact`}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}